import { useCallback, useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import {
  Check,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  HelpCircle,
  Trash2,
  X,
} from 'lucide-react';
import { loaderForBuildDoc, renderBuildDoc } from '@/shared/build-docs';
import { parseFrontmatter, type DocFrontmatter } from '@/shared/frontmatter';
import { GITHUB_REPO_SLUG } from '@/shared/github-repo';
import { MicTranscribeButton } from '@/design-system/MicTranscribeButton';

// Branch for "open in source" deep-links from the metadata panel. Hardcoded —
// the dev preview always runs against `dev`, and prod would still link to the
// live branch's view of the file.
const GH_BRANCH = 'dev';

// The 8 canonical engines. `Engine` suffix → kebab-case slug, dropping the
// "Engine" word (matches the expected `docs/engines/<slug>.md` naming).
function engineSlug(name: string): string {
  return name
    .trim()
    .replace(/\s+Engine$/, '')
    .toLowerCase()
    .replace(/\s+/g, '-');
}

// Engines whose canonical spec lives as a logic doc. Clicking the ENGINE
// chip should land on the rich spec, not the inventory anchor. Register an
// entry here when an engine's build doc is written (`'<Name> Engine':
// '<module>/<slug>'`); unknown engines fall back to the inventory anchor.
const ENGINE_LOGIC_DOC: Record<string, string> = {};

function engineHref(name: string): string {
  // Prefer the rich logic-doc spec; fall back to the inventory anchor for
  // unknown engines or when no canonical spec exists.
  const docPath = ENGINE_LOGIC_DOC[name.trim()];
  return docPath ? `/docs/logic/${docPath}` : `/docs/engines#${engineSlug(name)}`;
}

// `Chapter` → `chapter`, `ChapterSection` → `chapter-section`,
// `ConflictReference` → `conflict-reference`. Matches `docs/schema/<slug>.md`.
function schemaSlug(name: string): string {
  return name
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .toLowerCase();
}

// HelpAnchor — small `?` affordance next to a UI component that opens a side
// drawer showing the component's build doc from `docs/logic/`.
//
// Usage:
//   <HelpAnchor doc="studies/action-tracker" />
//   <HelpAnchor doc="reporting/report-view" section="data-inputs" />
//
// `doc` resolves to `docs/logic/<doc>.md` (extension omitted). `section` is the
// kebab-case slug of a heading inside that doc — when set, the drawer scrolls
// to that heading on open. Both are validated at build time by
// `scripts/check-help-anchors.mjs`.
//
// The drawer has a footer composer that posts notes to GitHub Issues via
// `POST /api/doc-notes`, mirroring the in-page comments backend. Existing
// notes load above the composer; reviewers can resolve, reopen, or delete
// their own notes.

type HelpAnchorProps = {
  doc: string;
  section?: string;
  label?: string;
};

const LS_AUTHOR = 'mock-ui-comment-author';
const NOTE_VERSION = 'v1';

// Mirror of NoteResponse in src/server/doc-notes/handler.ts.
export type NoteResponse = {
  number: number;
  htmlUrl: string;
  state: 'open' | 'closed';
  doc: string;
  section?: string;
  version: string;
  author: string;
  body: string;
  createdAt: string;
};

function prettifySlug(slug: string): string {
  // "trust-gauge" → "Trust gauge". Used only for the composer header label.
  if (!slug) return slug;
  const spaced = slug.replace(/-/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

async function unwrap<T>(res: Response): Promise<T> {
  if (res.status === 204) return undefined as T;
  const data = (await res.json().catch(() => null)) as
    | T
    | { error: string; detail?: string }
    | null;
  if (!res.ok) {
    if (data && typeof data === 'object' && 'error' in data) {
      const err = data as { error: string; detail?: string };
      throw new Error(err.detail ? `${err.error}: ${err.detail}` : err.error);
    }
    throw new Error(res.statusText || `HTTP ${res.status}`);
  }
  return data as T;
}

async function listDocNotes(doc: string): Promise<NoteResponse[]> {
  const res = await fetch(`/api/doc-notes?doc=${encodeURIComponent(doc)}&state=all`);
  return unwrap<NoteResponse[]>(res);
}

async function postDocNote(payload: {
  doc: string;
  section?: string;
  version: string;
  author: string;
  body: string;
}): Promise<NoteResponse> {
  const res = await fetch('/api/doc-notes', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return unwrap<NoteResponse>(res);
}

async function patchDocNote(
  number: number,
  status: 'resolved' | 'reopen',
): Promise<void> {
  const res = await fetch(`/api/doc-notes/${number}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status }),
  });
  await unwrap<void>(res);
}

async function deleteDocNote(number: number, author: string): Promise<void> {
  const res = await fetch(`/api/doc-notes/${number}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ author }),
  });
  await unwrap<void>(res);
}

function formatNoteDate(iso: string): string {
  // "2026-05-12T10:30:00Z" → "May 12". Drops year for current-year notes,
  // keeps it for older ones.
  try {
    const d = new Date(iso);
    const now = new Date();
    const opts: Intl.DateTimeFormatOptions =
      d.getUTCFullYear() === now.getUTCFullYear()
        ? { month: 'short', day: 'numeric' }
        : { year: 'numeric', month: 'short', day: 'numeric' };
    return d.toLocaleDateString(undefined, opts);
  } catch {
    return iso.slice(0, 10);
  }
}

export function HelpAnchor({ doc, section, label }: HelpAnchorProps) {
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState<string | null>(null);
  const [fm, setFm] = useState<DocFrontmatter | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Composer state
  const [composerOpen, setComposerOpen] = useState(false);
  const [noteBody, setNoteBody] = useState('');
  const [authorInput, setAuthorInput] = useState('');
  const [author, setAuthor] = useState<string | null>(null);
  const [posting, setPosting] = useState(false);
  const [postError, setPostError] = useState<string | null>(null);

  // Notes list state — fetched from GitHub Issues when the drawer opens.
  const [notes, setNotes] = useState<NoteResponse[]>([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [notesError, setNotesError] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<number | null>(null);

  const loader = useMemo(() => loaderForBuildDoc(doc), [doc]);

  // Hydrate author from localStorage as soon as the drawer opens so we can
  // identify the current reviewer's notes for delete affordance.
  useEffect(() => {
    if (!open) return;
    if (author !== null) return;
    const stored = typeof window !== 'undefined' ? window.localStorage.getItem(LS_AUTHOR) : null;
    if (stored) setAuthor(stored);
  }, [open, author]);

  // Reset composer state when the drawer closes (not on every section change).
  useEffect(() => {
    if (open) return;
    setComposerOpen(false);
    setNoteBody('');
    setAuthorInput('');
    setPosting(false);
    setPostError(null);
  }, [open]);

  // Fetch existing notes whenever the drawer opens on a new doc.
  useEffect(() => {
    if (!open) {
      setNotes([]);
      setNotesError(null);
      return;
    }
    let cancelled = false;
    setNotesLoading(true);
    setNotesError(null);
    listDocNotes(doc)
      .then((list) => {
        if (cancelled) return;
        // Dev mode hits Vite's static handler (not the worker), so the JSON
        // can be missing — guard against the parsed body being null.
        setNotes(Array.isArray(list) ? list : []);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setNotesError(err instanceof Error ? err.message : 'Failed to load notes');
      })
      .finally(() => {
        if (cancelled) return;
        setNotesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, doc]);

  const handleResolve = useCallback(async (n: number) => {
    setPendingAction(n);
    try {
      await patchDocNote(n, 'resolved');
      setNotes((prev) => prev.map((x) => (x.number === n ? { ...x, state: 'closed' } : x)));
    } catch (err) {
      setNotesError(err instanceof Error ? err.message : 'Failed to resolve');
    } finally {
      setPendingAction(null);
    }
  }, []);

  const handleReopen = useCallback(async (n: number) => {
    setPendingAction(n);
    try {
      await patchDocNote(n, 'reopen');
      setNotes((prev) => prev.map((x) => (x.number === n ? { ...x, state: 'open' } : x)));
    } catch (err) {
      setNotesError(err instanceof Error ? err.message : 'Failed to reopen');
    } finally {
      setPendingAction(null);
    }
  }, []);

  const handleDelete = useCallback(
    async (n: number) => {
      if (!author) return;
      setPendingAction(n);
      try {
        await deleteDocNote(n, author);
        setNotes((prev) => prev.filter((x) => x.number !== n));
      } catch (err) {
        setNotesError(err instanceof Error ? err.message : 'Failed to delete');
      } finally {
        setPendingAction(null);
      }
    },
    [author],
  );

  useEffect(() => {
    if (!open) return;
    if (!loader) {
      setError(`No doc registered at docs/logic/${doc}.md`);
      return;
    }
    let cancelled = false;
    setBody(null);
    setFm(null);
    setError(null);
    loader()
      .then((raw) => {
        if (cancelled) return;
        const parsed = parseFrontmatter(raw);
        setFm(parsed.fm);
        setBody(renderBuildDoc(raw));
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load doc');
      });
    return () => {
      cancelled = true;
    };
  }, [open, loader, doc]);

  // Scroll to the requested section after render.
  useEffect(() => {
    if (!open || !body || !section) return;
    const el = document.getElementById(section);
    if (el) el.scrollIntoView({ behavior: 'auto', block: 'start' });
  }, [open, body, section]);

  // Esc to close.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const titleLabel = label ?? doc;
  const composerTitle = section
    ? `Add a note on ${prettifySlug(section)}`
    : 'Add a note on this doc';

  async function handlePost() {
    setPostError(null);
    const trimmedBody = noteBody.trim();
    if (!trimmedBody) {
      setPostError('Note body is empty');
      return;
    }
    let effectiveAuthor = author;
    if (!effectiveAuthor) {
      const trimmedAuthor = authorInput.trim();
      if (!trimmedAuthor) {
        setPostError('Enter your name first');
        return;
      }
      effectiveAuthor = trimmedAuthor;
      try {
        if (typeof window !== 'undefined') {
          window.localStorage.setItem(LS_AUTHOR, trimmedAuthor);
        }
      } catch {
        // localStorage may be unavailable — proceed without persisting.
      }
      setAuthor(trimmedAuthor);
    }
    setPosting(true);
    try {
      const created = await postDocNote({
        doc,
        section,
        version: NOTE_VERSION,
        author: effectiveAuthor,
        body: trimmedBody,
      });
      setNotes((prev) => [created, ...prev]);
      setNoteBody('');
    } catch (err) {
      setPostError(err instanceof Error ? err.message : 'Failed to post');
    } finally {
      setPosting(false);
    }
  }

  return (
    <>
      <button
        type="button"
        data-review-mode="true"
        aria-label={`Open build doc for ${titleLabel}`}
        title="What is this? Open build doc"
        onClick={() => setOpen(true)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '20px',
          height: '20px',
          borderRadius: '50%',
          background: 'transparent',
          color: 'var(--ink-tertiary)',
          border: 'none',
          cursor: 'pointer',
          padding: 0,
          transition: 'color 120ms ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.color = 'var(--ink-primary)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.color = 'var(--ink-tertiary)';
        }}
      >
        <HelpCircle size={14} strokeWidth={1.75} />
      </button>

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={`Build doc — ${titleLabel}`}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1000,
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <div
            onClick={() => setOpen(false)}
            style={{
              position: 'absolute',
              inset: 0,
              background: 'var(--scrim)',
              backdropFilter: 'blur(2px)',
            }}
          />
          <aside
            style={{
              position: 'relative',
              width: 'min(520px, 92vw)',
              height: '100%',
              background: 'var(--surface-1)',
              borderLeft: '1px solid var(--hairline-strong)',
              boxShadow: '-8px 0 24px var(--shadow-warm-lg)',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <header
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '14px 18px',
                borderBottom: '1px solid var(--hairline)',
                flexShrink: 0,
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontSize: '10px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.14em',
                    fontWeight: 600,
                    color: 'var(--ink-tertiary)',
                    marginBottom: '2px',
                  }}
                >
                  Build doc
                </div>
                <div
                  style={{
                    fontSize: '13px',
                    color: 'var(--ink-secondary)',
                    fontVariantNumeric: 'tabular-nums',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  docs/logic/{doc}.md{section ? `#${section}` : ''}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                <a
                  href={`/docs/logic/${doc}${section ? `#${section}` : ''}`}
                  aria-label="Open in library"
                  title="Open in build-docs library"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '28px',
                    height: '28px',
                    borderRadius: '6px',
                    background: 'transparent',
                    color: 'var(--ink-secondary)',
                    textDecoration: 'none',
                  }}
                >
                  <ExternalLink size={14} strokeWidth={1.75} />
                </a>
                <button
                  type="button"
                  aria-label="Close"
                  onClick={() => setOpen(false)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '28px',
                    height: '28px',
                    borderRadius: '6px',
                    background: 'transparent',
                    color: 'var(--ink-secondary)',
                    border: 'none',
                    cursor: 'pointer',
                  }}
                >
                  <X size={16} strokeWidth={1.75} />
                </button>
              </div>
            </header>

            <div
              className="help-anchor-body"
              style={{
                flex: 1,
                overflowY: 'auto',
                padding: '24px 28px 32px',
                color: 'var(--ink-secondary)',
                fontSize: '13.5px',
                lineHeight: 1.65,
              }}
            >
              {error && (
                <div style={{ color: 'var(--state-blocked)', fontSize: '13px' }}>
                  {error}
                </div>
              )}
              {!error && body === null && (
                <div style={{ color: 'var(--ink-tertiary)', fontSize: '12px' }}>
                  Loading…
                </div>
              )}
              {!error && body !== null && (
                <>
                  <MetadataPanel fm={fm} doc={doc} />
                  <div dangerouslySetInnerHTML={{ __html: body }} />
                </>
              )}

              <NotesPanel
                notes={notes}
                loading={notesLoading}
                error={notesError}
                currentAuthor={author}
                pendingAction={pendingAction}
                onResolve={handleResolve}
                onReopen={handleReopen}
                onDelete={handleDelete}
              />
            </div>

            {/* Composer — sticky footer. */}
            <div
              style={{
                flexShrink: 0,
                borderTop: '1px solid var(--hairline)',
                background: 'var(--surface-2)',
              }}
            >
              {!composerOpen && (
                <button
                  type="button"
                  onClick={() => setComposerOpen(true)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 18px',
                    background: 'transparent',
                    border: 'none',
                    color: 'var(--ink-secondary)',
                    fontSize: '12px',
                    fontWeight: 500,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}
                >
                  <span>{composerTitle}</span>
                  <ChevronUp size={14} strokeWidth={1.75} />
                </button>
              )}
              {composerOpen && (
                <div style={{ padding: '12px 18px 14px' }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: '8px',
                    }}
                  >
                    <span
                      style={{
                        fontSize: '11px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        fontWeight: 600,
                        color: 'var(--ink-tertiary)',
                      }}
                    >
                      {composerTitle}
                    </span>
                    <button
                      type="button"
                      onClick={() => setComposerOpen(false)}
                      aria-label="Collapse composer"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '20px',
                        height: '20px',
                        background: 'transparent',
                        border: 'none',
                        color: 'var(--ink-tertiary)',
                        cursor: 'pointer',
                        padding: 0,
                      }}
                    >
                      <ChevronDown size={14} strokeWidth={1.75} />
                    </button>
                  </div>
                  {author === null && (
                    <input
                      type="text"
                      placeholder="Your name"
                      value={authorInput}
                      onChange={(e) => setAuthorInput(e.target.value)}
                      disabled={posting}
                      maxLength={60}
                      style={{
                        width: '100%',
                        padding: '6px 10px',
                        marginBottom: '8px',
                        fontSize: '12.5px',
                        background: 'var(--surface-1)',
                        border: '1px solid var(--hairline)',
                        borderRadius: '4px',
                        color: 'var(--ink-primary)',
                        fontFamily: 'inherit',
                        outline: 'none',
                      }}
                    />
                  )}
                  <textarea
                    placeholder="What would improve this doc?"
                    value={noteBody}
                    onChange={(e) => setNoteBody(e.target.value)}
                    disabled={posting}
                    maxLength={2000}
                    rows={3}
                    autoFocus
                    style={{
                      width: '100%',
                      padding: '8px 10px',
                      fontSize: '13px',
                      background: 'var(--surface-1)',
                      border: '1px solid var(--hairline)',
                      borderRadius: '4px',
                      color: 'var(--ink-primary)',
                      fontFamily: 'inherit',
                      resize: 'vertical',
                      lineHeight: 1.5,
                      outline: 'none',
                    }}
                  />
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginTop: '8px',
                      gap: '8px',
                    }}
                  >
                    <span
                      style={{
                        fontSize: '11px',
                        color: postError ? 'var(--state-blocked)' : 'var(--ink-tertiary)',
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {postError ?? `${noteBody.length}/2000`}
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <MicTranscribeButton value={noteBody} onChange={setNoteBody} />
                      <button
                        type="button"
                        onClick={handlePost}
                        disabled={posting || noteBody.trim().length === 0}
                        style={{
                          padding: '6px 14px',
                          fontSize: '12px',
                          fontWeight: 500,
                          background: posting || noteBody.trim().length === 0
                            ? 'var(--surface-3)'
                            : 'var(--accent)',
                          color: posting || noteBody.trim().length === 0
                            ? 'var(--ink-tertiary)'
                            : 'var(--accent-text)',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: posting || noteBody.trim().length === 0 ? 'default' : 'pointer',
                          fontFamily: 'inherit',
                        }}
                      >
                        {posting ? 'Posting…' : 'Post note'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </aside>
        </div>
      )}
    </>
  );
}

// MetadataPanel — renders the 4-layer metadata block (engine, transformation,
// schema, consumes) parsed from a logic doc's YAML frontmatter. Sits inside
// `.help-anchor-body` above the rendered markdown. Returns `null` when there
// is nothing to show so the drawer falls back to its pre-Phase-F look.
type MetadataPanelProps = {
  fm: DocFrontmatter | null;
  doc: string;
};

function MetadataPanel({ fm, doc: _doc }: MetadataPanelProps) {
  if (!fm) return null;
  // `None` is the sentinel value some docs use for "no engine backs this
  // surface". Treat it as absent so we don't render a dead chip.
  const engineValue = fm.engine && fm.engine.trim().toLowerCase() !== 'none' ? fm.engine : undefined;
  const hasEngine = Boolean(engineValue);
  const hasTransformation = Boolean(fm.transformation);
  const hasSchema = Boolean(fm.schema && fm.schema.length > 0);
  const hasConsumes = Boolean(fm.consumes && fm.consumes.length > 0);
  if (!hasEngine && !hasTransformation && !hasSchema && !hasConsumes) {
    return null;
  }
  return (
    <section
      aria-label="4-layer metadata"
      style={{
        marginBottom: '20px',
        paddingBottom: '16px',
        borderBottom: '1px solid var(--hairline)',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
      }}
    >
      {hasEngine && (
        <MetadataRow label="Engine">
          <a
            href={engineHref(engineValue!)}
            target="_blank"
            rel="noreferrer"
            style={metadataLinkStyle}
          >
            {engineValue}
            <ExternalLink size={11} strokeWidth={1.75} style={{ marginLeft: '4px' }} />
          </a>
        </MetadataRow>
      )}
      {hasTransformation && (
        <MetadataRow label="Transformation">
          <a
            href={`https://github.com/${GITHUB_REPO_SLUG}/blob/${GH_BRANCH}/${fm.transformation!}`}
            target="_blank"
            rel="noreferrer"
            title={fm.transformation}
            style={metadataLinkStyle}
          >
            {fm.transformation!.split('/').pop()}
            <ExternalLink size={11} strokeWidth={1.75} style={{ marginLeft: '4px' }} />
          </a>
        </MetadataRow>
      )}
      {hasSchema && (
        <MetadataRow label="Schema">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
            {fm.schema!.map((entity, idx) => (
              <span key={entity} style={{ display: 'inline-flex', alignItems: 'center' }}>
                <a
                  href={`/docs/schema/${schemaSlug(entity)}`}
                  target="_blank"
                  rel="noreferrer"
                  style={metadataChipStyle}
                >
                  {entity}
                </a>
                {idx < fm.schema!.length - 1 && (
                  <span
                    aria-hidden
                    style={{
                      marginLeft: '6px',
                      color: 'var(--ink-tertiary)',
                      fontSize: '12px',
                    }}
                  >
                    ·
                  </span>
                )}
              </span>
            ))}
          </div>
        </MetadataRow>
      )}
      {hasConsumes && (
        <MetadataRow label="Consumes">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
            {fm.consumes!.map((entry, idx) => (
              <span key={`${entry.engine}-${entry.handshake}`} style={{ display: 'inline-flex', alignItems: 'center' }}>
                <a
                  href={engineHref(entry.engine)}
                  target="_blank"
                  rel="noreferrer"
                  style={metadataLinkStyle}
                >
                  {entry.engine}
                </a>
                <span style={{ color: 'var(--ink-tertiary)', margin: '0 4px' }}>(</span>
                <a
                  href="/docs/engines#cross-engine-handshakes"
                  target="_blank"
                  rel="noreferrer"
                  title={`Handshake ${entry.handshake} — see Cross-engine handshakes in engines.md`}
                  style={metadataLinkStyle}
                >
                  {entry.handshake}
                </a>
                <span style={{ color: 'var(--ink-tertiary)' }}>)</span>
                {idx < fm.consumes!.length - 1 && (
                  <span aria-hidden style={{ marginLeft: '6px', color: 'var(--ink-tertiary)' }}>
                    ·
                  </span>
                )}
              </span>
            ))}
          </div>
        </MetadataRow>
      )}
    </section>
  );
}

function MetadataRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '92px 1fr',
        alignItems: 'baseline',
        gap: '12px',
      }}
    >
      <div
        style={{
          fontSize: '10px',
          textTransform: 'uppercase',
          letterSpacing: '0.14em',
          fontWeight: 600,
          color: 'var(--ink-tertiary)',
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: '12.5px', color: 'var(--ink-secondary)', minWidth: 0 }}>
        {children}
      </div>
    </div>
  );
}

const metadataLinkStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  color: 'var(--ink-primary)',
  textDecoration: 'none',
  fontWeight: 500,
  borderBottom: '1px solid var(--hairline)',
  paddingBottom: '1px',
  transition: 'border-color 120ms ease, color 120ms ease',
};

const metadataChipStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '2px 8px',
  fontSize: '11.5px',
  fontWeight: 500,
  background: 'var(--surface-2)',
  border: '1px solid var(--hairline)',
  borderRadius: '999px',
  color: 'var(--ink-primary)',
  textDecoration: 'none',
  fontFamily: 'inherit',
};

type NotesPanelProps = {
  notes: NoteResponse[];
  loading: boolean;
  error: string | null;
  currentAuthor: string | null;
  pendingAction: number | null;
  onResolve: (n: number) => void;
  onReopen: (n: number) => void;
  onDelete: (n: number) => void;
};

function NotesPanel({
  notes,
  loading,
  error,
  currentAuthor,
  pendingAction,
  onResolve,
  onReopen,
  onDelete,
}: NotesPanelProps) {
  const openCount = notes.filter((n) => n.state === 'open').length;

  return (
    <section
      aria-label="Notes"
      style={{
        marginTop: '32px',
        paddingTop: '20px',
        borderTop: '1px solid var(--hairline)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: '8px',
          marginBottom: '12px',
        }}
      >
        <h3
          style={{
            margin: 0,
            fontSize: '11px',
            textTransform: 'uppercase',
            letterSpacing: '0.14em',
            fontWeight: 600,
            color: 'var(--ink-tertiary)',
          }}
        >
          Notes
        </h3>
        <span
          style={{
            fontSize: '11px',
            color: 'var(--ink-tertiary)',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {loading ? 'Loading…' : `${openCount} open · ${notes.length} total`}
        </span>
      </div>

      {error && (
        <div
          style={{
            fontSize: '12px',
            color: 'var(--state-blocked)',
            marginBottom: '8px',
          }}
        >
          {error}
        </div>
      )}

      {!loading && notes.length === 0 && !error && (
        <div style={{ fontSize: '12px', color: 'var(--ink-tertiary)' }}>
          No notes yet. Be the first to leave one below.
        </div>
      )}

      <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {notes.map((note) => {
          const resolved = note.state === 'closed';
          const isOwner = currentAuthor !== null && note.author.trim() === currentAuthor.trim();
          const busy = pendingAction === note.number;
          return (
            <li
              key={note.number}
              style={{
                padding: '10px 12px',
                background: resolved ? 'var(--surface-2)' : 'var(--surface-1)',
                border: '1px solid var(--hairline)',
                borderRadius: '6px',
                opacity: resolved ? 0.6 : 1,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  justifyContent: 'space-between',
                  gap: '8px',
                  marginBottom: '4px',
                }}
              >
                <div
                  style={{
                    fontSize: '12px',
                    fontWeight: 600,
                    color: 'var(--ink-primary)',
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {note.author}
                </div>
                <div
                  style={{
                    fontSize: '11px',
                    color: 'var(--ink-tertiary)',
                    fontVariantNumeric: 'tabular-nums',
                    flexShrink: 0,
                  }}
                >
                  {formatNoteDate(note.createdAt)}
                  {note.section ? ` · #${note.section}` : ''}
                </div>
              </div>
              <div
                style={{
                  fontSize: '13px',
                  color: 'var(--ink-secondary)',
                  whiteSpace: 'pre-wrap',
                  lineHeight: 1.55,
                }}
              >
                {note.body}
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '6px',
                  marginTop: '8px',
                }}
              >
                {resolved ? (
                  <button
                    type="button"
                    onClick={() => onReopen(note.number)}
                    disabled={busy}
                    style={noteActionBtnStyle(busy)}
                  >
                    Reopen
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => onResolve(note.number)}
                    disabled={busy}
                    style={noteActionBtnStyle(busy)}
                    aria-label="Mark resolved"
                  >
                    <Check size={12} strokeWidth={2} style={{ display: 'block' }} />
                    <span>Resolve</span>
                  </button>
                )}
                {isOwner && (
                  <button
                    type="button"
                    onClick={() => onDelete(note.number)}
                    disabled={busy}
                    style={noteActionBtnStyle(busy)}
                    aria-label="Delete note"
                  >
                    <Trash2 size={12} strokeWidth={2} style={{ display: 'block' }} />
                    <span>Delete</span>
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function noteActionBtnStyle(busy: boolean): CSSProperties {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 8px',
    fontSize: '11px',
    fontWeight: 500,
    background: 'transparent',
    border: '1px solid var(--hairline)',
    borderRadius: '4px',
    color: 'var(--ink-secondary)',
    cursor: busy ? 'default' : 'pointer',
    opacity: busy ? 0.5 : 1,
    fontFamily: 'inherit',
  };
}
